import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Pagination,
  InputAdornment, CircularProgress, Link
} from "@mui/material";
import { Add, Delete, Search, Clear, Refresh } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { aiCallApi, canonicalMobile, dhaka, formatNumber } from "./useAICalling";
import { AICallingPage, Loading, ErrorState, ConfirmDialog } from "./AICallingComponents";

const AddDialog = ({ open, onClose, onAdded }) => {
  const [mobile, setMobile] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMobile("");
      setReason("");
      setError("");
    }
  }, [open]);

  const save = async () => {
    const number = canonicalMobile(mobile);
    if (!number) return setError("Enter a Bangladeshi mobile number (01XXXXXXXXX)");
    setBusy(true);
    const res = await aiCallApi.addDnc(number, reason.trim());
    setBusy(false);
    if (!res.ok) return setError(res.error);
    toast.success(res.message || `${number} won't be called`);
    onAdded();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add to do-not-call</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This number will never get an AI call or a promotional SMS from the channel ladder.
        </Typography>
        <TextField
          autoFocus fullWidth label="Mobile" placeholder="01XXXXXXXXX" margin="dense"
          value={mobile} onChange={(e) => { setMobile(e.target.value); setError(""); }}
          inputProps={{ inputMode: "tel", maxLength: 20 }}
          error={!!error} helperText={error || "01XXXXXXXXX"}
        />
        <TextField
          fullWidth label="Reason" margin="dense" multiline minRows={2}
          value={reason} onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 300 }} helperText={`${reason.length}/300`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !mobile.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AICallingDnc = () => {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await aiCallApi.dnc({ q, page });
    setLoading(false);
    if (!res.ok) return setError(res.error);
    setError("");
    setRows(Array.isArray(res.data) ? res.data : []);
    setPagination(res.body?.pagination || { total: 0, totalPages: 1 });
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    setPage(1);
    setQ(search.replace(/\D/g, ""));
  };

  const remove = async () => {
    const res = await aiCallApi.removeDnc(removing._id);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Removed");
    load();
    return true;
  };

  return (
    <AICallingPage
      title="Do Not Call"
      subtitle="Numbers the AI agent never calls"
      actions={
        <>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>Add number</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </>
      }
    >
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search by number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
          inputProps={{ inputMode: "tel" }}
          sx={{ flex: "1 1 240px", maxWidth: 360 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: search ? (
              <IconButton size="small" onClick={() => { setSearch(""); setQ(""); setPage(1); }} aria-label="Clear search">
                <Clear fontSize="small" />
              </IconButton>
            ) : undefined,
          }}
        />
        <Button variant="outlined" onClick={applySearch}>Search</Button>
        <Typography variant="body2" color="text.secondary">{formatNumber(pagination.total)} number{pagination.total === 1 ? "" : "s"}</Typography>
      </Box>

      {error && !rows.length ? (
        <ErrorState error={error} onRetry={load} />
      ) : loading && !rows.length ? (
        <Loading />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 820, opacity: loading ? 0.6 : 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Mobile</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Added by</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {q ? "No number matches." : "The list is empty."}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.mobile}</TableCell>
                    <TableCell>{r.name || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }} lang="bn">{r.reason || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      {r.source === "agent" ? (
                        <>
                          <Chip size="small" color="info" variant="outlined" label="Asked on a call" />
                          {r.callId && (
                            <Link component={RouterLink} to={`/ai-calling/calls/${r.callId}`} variant="caption" display="block" sx={{ mt: 0.5 }}>
                              Open call
                            </Link>
                          )}
                        </>
                      ) : (
                        <Chip size="small" variant="outlined" label="Admin" />
                      )}
                    </TableCell>
                    <TableCell>{r.addedByName || (r.source === "agent" ? "AI agent" : "—")}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{dhaka(r.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from the list">
                        <IconButton size="small" color="error" onClick={() => setRemoving(r)} aria-label="Remove">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={pagination.totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      <AddDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          load();
        }}
      />

      <ConfirmDialog
        open={!!removing}
        title="Remove from do-not-call?"
        confirmText="Remove"
        confirmColor="error"
        onClose={() => setRemoving(null)}
        onConfirm={remove}
      >
        {removing
          ? `${removing.mobile}${removing.name ? ` (${removing.name})` : ""} can get AI calls again.${removing.source === "agent" ? " This member asked on a call not to be called." : ""}`
          : ""}
      </ConfirmDialog>
    </AICallingPage>
  );
};

export default AICallingDnc;
